<script setup lang="ts" generic="T">
import { useActiveScroll } from 'vue-use-active-scroll';

export type StickyNavigationItem<T> = {
  ref: HTMLElement;
} & T;

type NavigationItem = StickyNavigationItem<T> & { anchor: string };

const props = withDefaults(defineProps<{
  items: StickyNavigationItem<T>[];
  jumpToFirst?: boolean;
  jumpToLast?: boolean;
}>(), {
  jumpToFirst: false,
  jumpToLast: false,
});

const emit = defineEmits<{
  'item-clicked': [event: MouseEvent, anchor: string];
}>();

/********************
* COMPOSITIONS      *
********************/
const route = useRoute();

/********************
 * REFS & VARS       *
 ********************/
const scrollableContainerRef = useTemplateRef('scrollableContainerRef');
const scrollableRef = useTemplateRef('scrollableRef');
const navigationItems = ref <NavigationItem[]> ();
const targets = ref<HTMLElement[]>([]);

const { showRightGradient, showLeftGradient } = useScrollable({
  scrollableContainerRef,
  scrollableRef,
});

const { setActive, activeId } = useActiveScroll(targets, {
  jumpToFirst: props.jumpToFirst,
  jumpToLast: props.jumpToLast,
  replaceHash: false,
  overlayHeight: 48,
});

watch(() => props.items, onItemsChange, { immediate: true });
watch(() => activeId.value, onActiveIdChange, { immediate: true });

function onItemsChange(newValue: StickyNavigationItem<T>[]) {
  if (import.meta.server) {
    return;
  }

  const nextTargets: HTMLElement[] = [];
  const nextNavigationItems: NavigationItem[] = [];

  for (const item of newValue) {
    if (!item.ref) {
      console.warn('StickyNavigationItem is missing ref', item);
      continue;
    }
    nextTargets.push(item.ref);
    nextNavigationItems.push({
      ...item,
      anchor: item.ref.getAttribute('id') || '',
      ref: item.ref,
    });
  }

  targets.value = nextTargets;
  navigationItems.value = nextNavigationItems;
}

function onActiveIdChange(activeId: string) {
  if (scrollableRef.value && activeId) {
    const activeAnchor = scrollableRef.value.querySelectorAll(`[data-id=${activeId}]`)[0];

    if (!activeAnchor) {
      return;
    }
    nextTick(() => {
      if (!scrollableRef.value) {
        return;
      }
      scrollableRef.value.scroll?.({
        left: (activeAnchor as HTMLElement).offsetLeft - window.innerWidth / 2 + activeAnchor.getBoundingClientRect().width / 2,
        behavior: 'smooth',
      });
    });
  }
}

function onNavItemClicked(event: MouseEvent, item: NavigationItem) {
  setActive(item.anchor);
  emit('item-clicked', event, item.anchor);
}
</script>

<template>
  <div
    ref="scrollableContainerRef"
    class="sticky top-0 z-30 min-h-[46px] border-b-2 border-dark-green bg-gray-10"
  >
    <div
      ref="scrollableRef"
      class="navigation__scrollable overflow-auto"
      style="-webkit-overflow-scrolling: touch; -ms-overflow-style: none; scrollbar-width: none"
    >
      <client-only>
        <nav class="flex flex-row text-center lg:justify-center">
          <NuxtLink
            v-for="item in navigationItems"
            :key="item.anchor"
            :to="{ name: route.name, params: route.params, query: route.query, hash: `#${item.anchor}`, replace: true }"
            class="whitespace-nowrap border-y-[3px] px-6 py-2 text-dark-green hover:border-b-dark-green hover:no-underline"
            :class="[item.anchor === activeId ? 'border-b-dark-green border-t-transparent' : 'border-transparent']"
            :data-id="item.anchor"
            @click.capture.prevent="onNavItemClicked($event, item)"
          >
            <slot name="item" v-bind="item" />
          </NuxtLink>
        </nav>
      </client-only>
    </div>
    <div
      v-if="showLeftGradient"
      class="absolute left-0 top-0 h-[calc(100%-2px)] w-[30px]"
      style="background: linear-gradient(to left, rgba(255, 255, 255, 0) 0%, rgb(var(--color-gray-10)) 100%)"
    />
    <div
      v-if="showRightGradient"
      class="absolute right-0 top-0 h-[calc(100%-2px)] w-[30px]"
      style="background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgb(var(--color-gray-10)) 100%)"
    />
  </div>
</template>
